import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  isFedexOneRateEligibleService,
  type CarrierAndService,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { lengthUnitDisplayName } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  CustomParcelType,
  fedexOneRateParcelDisplayName,
  getMatchingOneRateParcel,
  OutboundParcel,
  ParcelType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useEffect, useMemo, useState } from "react";
import { getDimensionalWeightThresholdWarning } from "./dimensional-weight-warning";

type ParcelDimension = "length" | "width" | "height";

const DIMENSIONAL_WEIGHT_WARNING_DISMISSED_KEY_PREFIX =
  "redo-parcel-dimensional-weight-threshold-warning-dismissed";

function getDimensionalWeightWarningStorageKey(
  parcel: OutboundParcel | null,
): string {
  return `${DIMENSIONAL_WEIGHT_WARNING_DISMISSED_KEY_PREFIX}:${getParcelDimensionStorageKey(parcel)}`;
}

function getParcelDimensionStorageKey(parcel: OutboundParcel | null): string {
  if (!parcel || parcel.parcelType !== ParcelType.CUSTOM) {
    return "unknown";
  }

  return [parcel.lengthUnit, parcel.length, parcel.width, parcel.height].join(
    ":",
  );
}

function useLocalStorageDismissedState(storageKey: string) {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(storageKey) === "true";
  });

  useEffect(() => {
    setDismissed(localStorage.getItem(storageKey) === "true");
  }, [storageKey]);

  const dismiss = useHandler(() => {
    localStorage.setItem(storageKey, "true");
    setDismissed(true);
  });

  return [dismissed, dismiss] as const;
}

export const ParcelSizeWidget = memo(function ParcelSizeWidget({
  disabled,
  hideTypeSelector,
  hideAdditionalSettings,
  keepParcelOnDimensionEdit = false,
  selectedParcel,
  selectedRate,
  updateParcel,
}: {
  disabled: boolean;
  hideTypeSelector?: boolean;
  hideAdditionalSettings?: boolean;
  keepParcelOnDimensionEdit?: boolean;
  selectedParcel: OutboundParcel | null;
  selectedRate?: CarrierAndService | null;
  updateParcel: (parcel: Partial<OutboundParcel>) => void;
}) {
  const dimensionUnit = useMemo(() => {
    if (selectedParcel?.parcelType !== ParcelType.CUSTOM) {
      return "";
    }
    return selectedParcel.lengthUnit;
  }, [selectedParcel]);

  const onDimensionChange = useHandler(
    (dimension: ParcelDimension, value: number) => {
      updateParcel(
        keepParcelOnDimensionEdit
          ? { [dimension]: value }
          : { [dimension]: value, name: "Custom" },
      );
    },
  );

  const onParcelTypeChange = useHandler((type: CustomParcelType) => {
    updateParcel({ type });
  });

  const customParcelTypeLabel = (type: CustomParcelType) => {
    switch (type) {
      case CustomParcelType.BOX:
        return "Box";
      case CustomParcelType.SOFT_PACK:
        return "Soft Pack";
      case CustomParcelType.ENVELOPE:
        return "Envelope";
      default:
        assertNever(type);
    }
  };

  const team = useRequiredContext(TeamContext);
  const oneRateMatch = useMemo(() => {
    if (
      !selectedRate ||
      !isFedexOneRateEligibleService(selectedRate.service) ||
      selectedParcel?.parcelType !== ParcelType.CUSTOM
    ) {
      return null;
    }
    return getMatchingOneRateParcel(
      {
        length: selectedParcel.length,
        width: selectedParcel.width,
        height: selectedParcel.height,
        lengthUnit: selectedParcel.lengthUnit,
        parcelType: selectedParcel.type,
      },
      team._id.toString(),
    );
  }, [selectedRate, selectedParcel, team._id]);

  const oneRateParcelLabel = useMemo(() => {
    if (
      !selectedRate ||
      !isFedexOneRateEligibleService(selectedRate.service) ||
      selectedParcel?.parcelType !== ParcelType.CUSTOM
    ) {
      return null;
    }
    if (!oneRateMatch) {
      return "Package not rated for FedEx One Rate";
    }
    return `Rated as a ${fedexOneRateParcelDisplayName(oneRateMatch.parcel)} for FedEx One Rate`;
  }, [selectedRate, selectedParcel, oneRateMatch]);

  const dimensionalWeightWarning = useMemo(
    () =>
      getDimensionalWeightThresholdWarning({
        parcel: selectedParcel,
        selectedRate,
      }),
    [selectedParcel, selectedRate],
  );
  const dimensionalWeightWarningDismissedStorageKey = useMemo(
    () => getDimensionalWeightWarningStorageKey(selectedParcel),
    [selectedParcel],
  );
  const [dimensionalWeightWarningDismissed, dismissDimensionalWeightWarning] =
    useLocalStorageDismissedState(dimensionalWeightWarningDismissedStorageKey);
  const shouldShowDimensionalWeightWarning = Boolean(
    dimensionalWeightWarning && !dimensionalWeightWarningDismissed,
  );

  const [expanded, setExpanded] = useState(false);

  const dimensionSuffix =
    dimensionUnit === "" ? "" : lengthUnitDisplayName(dimensionUnit);

  return (
    <Flex dir="column" gap="xs">
      <Flex dir="column" flex="1" gap="md">
        {selectedParcel && (
          <>
            <Text fontSize="xs">
              Size {dimensionSuffix ? `(${dimensionSuffix})` : ""}
            </Text>
            {selectedParcel.parcelType === ParcelType.CUSTOM ? (
              <>
                <Flex>
                  <RedoIncrementDecrement
                    description="Length"
                    disabled={disabled}
                    fullWidth
                    min={0}
                    placeholder="0"
                    setValue={(value) => onDimensionChange("length", value)}
                    size="small"
                    suffix={dimensionSuffix}
                    value={getParcelDimension(selectedParcel, "length")}
                  />
                  <RedoIncrementDecrement
                    description="Width"
                    disabled={disabled}
                    fullWidth
                    min={0}
                    placeholder="0"
                    setValue={(value) => onDimensionChange("width", value)}
                    size="small"
                    suffix={dimensionSuffix}
                    value={getParcelDimension(selectedParcel, "width")}
                  />
                  <RedoIncrementDecrement
                    description="Height"
                    disabled={disabled}
                    fullWidth
                    min={0}
                    placeholder="0"
                    setValue={(value) => onDimensionChange("height", value)}
                    size="small"
                    suffix={dimensionSuffix}
                    value={getParcelDimension(selectedParcel, "height")}
                  />
                </Flex>
                {!hideTypeSelector && (
                  <RedoSingleSelectDropdownInput<CustomParcelType>
                    disabled={disabled}
                    options={Object.values(CustomParcelType).map((type) => ({
                      id: type,
                      type: "text",
                      text: customParcelTypeLabel(type),
                      value: type,
                    }))}
                    optionSelected={({ item: value }) =>
                      onParcelTypeChange(value.value)
                    }
                    selectedItem={{
                      id: selectedParcel.type,
                      type: "text",
                      text: customParcelTypeLabel(selectedParcel.type),
                      value: selectedParcel.type,
                    }}
                    size="xs"
                  />
                )}
                {oneRateParcelLabel && (
                  <Text fontSize="xs" textColor="secondary">
                    {oneRateParcelLabel}
                  </Text>
                )}
                {oneRateMatch?.dimensionWarning && (
                  <Text fontSize="xs" textColor="warning">
                    Volume matches One Rate requirements, but individual
                    dimensions exceed tolerance.{" "}
                    <a
                      href="https://www.fedex.com/en-us/shipping/one-rate.html"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline" }}
                      target="_blank"
                    >
                      Learn more
                    </a>
                  </Text>
                )}
                {shouldShowDimensionalWeightWarning && (
                  <RedoAlert
                    closeButton={{
                      showCloseButton: true,
                      onClose: dismissDimensionalWeightWarning,
                    }}
                    color="warning"
                    description="This package is within 1% of the selected carrier's billable-size threshold. Double-check length, width, and height before buying the label; a small carrier remeasurement can raise the cost or create an adjustment."
                    fullWidth
                    Icon={AlertTriangleIcon}
                    title="Close to dimensional weight threshold"
                  />
                )}
                {!hideAdditionalSettings && (
                  <Flex
                    align="center"
                    cursor="pointer"
                    justify="space-between"
                    onClick={() => setExpanded(!expanded)}
                    pt="md"
                  >
                    <Text fontSize="xs" fontWeight="semibold">
                      Additional package settings
                    </Text>
                    <RedoButton
                      IconLeading={
                        expanded ? TriangleDownIcon : TriangleRightIcon
                      }
                      size="xs"
                    />
                  </Flex>
                )}
                {!hideAdditionalSettings && expanded && (
                  <Flex dir="column" gap="md">
                    <RedoCheckbox
                      description={
                        <Flex align="center" gap="xs">
                          <Text fontSize="xs" textColor="secondary">
                            Requires additional handling
                          </Text>
                          <InfoTooltip
                            boxSize={16}
                            tooltip={{
                              title:
                                "Choose to pay an additional handling charge up front, to potentially avoid additional handling surcharges",
                            }}
                          />
                        </Flex>
                      }
                      disabled={disabled}
                      setValue={(value) => {
                        updateParcel({ additionalHandlingIndicator: !!value });
                      }}
                      size="xs"
                      value={
                        selectedParcel?.additionalHandlingIndicator ?? false
                      }
                    />
                    <RedoCheckbox
                      description={
                        <Flex align="center" gap="xs">
                          <Text fontSize="xs" textColor="secondary">
                            Mark as large package
                          </Text>
                          <InfoTooltip
                            boxSize={16}
                            tooltip={{
                              title:
                                "Choose to pay a large package fee up front, to potentially avoid additional large package surcharges (UPS only)",
                            }}
                          />
                        </Flex>
                      }
                      disabled={disabled}
                      setValue={(value) => {
                        updateParcel({ largePackageIndicator: !!value });
                      }}
                      size="xs"
                      value={selectedParcel?.largePackageIndicator ?? false}
                    />
                    <RedoCheckbox
                      description={
                        <Flex align="center" gap="xs">
                          <Text fontSize="xs" textColor="secondary">
                            Shipper release
                          </Text>
                          <InfoTooltip
                            boxSize={16}
                            tooltip={{
                              title:
                                "Deliver on the first attempt even if recipient is not available (UPS only)",
                            }}
                          />
                        </Flex>
                      }
                      disabled={disabled}
                      setValue={(value) => {
                        updateParcel({ shipperReleaseIndicator: !!value });
                      }}
                      size="xs"
                      value={selectedParcel?.shipperReleaseIndicator ?? false}
                    />
                  </Flex>
                )}
              </>
            ) : (
              <Text fontSize="xs">
                {selectedParcel.dimensions || "Unknown "}
              </Text>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
});

const getParcelDimension = (
  parcel: OutboundParcel,
  dimension: ParcelDimension,
): number => {
  if (parcel.parcelType === ParcelType.CUSTOM) {
    return parcel[dimension];
  }
  return 0;
};
